import api from './api';

export type ActorType = 'company' | 'university' | 'research_lab' | 'individual' | 'government' | 'other';
export type SizeCategory = 'large' | 'intermediate' | 'sme' | 'unknown';
export type LandscapeStatus = 'draft' | 'extracted' | 'normalizing' | 'normalized' | 'tech_mapped' | 'generating' | 'finalized';
export const RUNNING_STATUSES: LandscapeStatus[] = ['normalizing', 'generating'];

// ISO-2 codes that belong to the EU or UK (used for the country-zone filter)
export const EU_UK_COUNTRIES = new Set([
  'AT','BE','BG','CY','CZ','DE','DK','EE','ES','FI','FR','GR','HR',
  'HU','IE','IT','LT','LU','LV','MT','NL','PL','PT','RO','SE','SI','SK',
  'GB',  // United Kingdom
  'CH','NO','IS','LI',  // EEA / associated: Switzerland, Norway, Iceland, Liechtenstein
]);

export type CountryZone = 'FR' | 'EU_UK' | 'other' | 'unknown';

export function countryZone(isoCode: string | null): CountryZone {
  if (!isoCode) return 'unknown';
  if (isoCode === 'FR') return 'FR';
  if (EU_UK_COUNTRIES.has(isoCode)) return 'EU_UK';
  return 'other';
}

export interface Actor {
  actor_id: string;
  canonical_name: string;
  aliases: string[];
  actor_type: ActorType;
  country: string | null;
  parent_group: string | null;
  size_category: SizeCategory | null;
  document_ids: string[];
  roles: string[];
  patent_count: number;
  publication_count: number;
  other_count: number;
  description: string | null;
  sector: string | null;
  technology_areas: string[];
  website: string | null;
  confidence: number;
  source: string;
}

export interface ActorUpdate {
  canonical_name?: string;
  actor_type?: ActorType;
  country?: string | null;
  parent_group?: string | null;
  size_category?: SizeCategory | null;
  description?: string | null;
  sector?: string | null;
  website?: string | null;
}

export interface ActorLandscape {
  id: string;
  kb_id: string;
  name: string;
  created_by: string;
  client_id: string;
  created_at: string;
  last_updated: string;
  status: LandscapeStatus;
  generation_steps: string[];
  actors: Actor[];
  report_markdown: string | null;
  error_message: string | null;
}

export interface ActorLandscapeSummary {
  id: string;
  kb_id: string;
  name: string;
  status: LandscapeStatus;
  actor_count: number;
  created_at: string;
  last_updated: string;
}

export interface CreateLandscapeRequest {
  name: string;
}

export interface ReportRequest {
  custom_instructions?: string | null;
}

export const actorLandscapeService = {
  async create(kbId: string, data: CreateLandscapeRequest): Promise<ActorLandscape> {
    const res = await api.post<ActorLandscape>(`/knowledge-bases/${kbId}/actor-landscapes`, data);
    return res.data;
  },

  async listForKB(kbId: string): Promise<ActorLandscapeSummary[]> {
    const res = await api.get<ActorLandscapeSummary[]>(`/knowledge-bases/${kbId}/actor-landscapes`);
    return res.data;
  },

  async getById(landscapeId: string): Promise<ActorLandscape> {
    const res = await api.get<ActorLandscape>(`/actor-landscapes/${landscapeId}`);
    return res.data;
  },

  async delete(landscapeId: string): Promise<void> {
    await api.delete(`/actor-landscapes/${landscapeId}`);
  },

  async updateActor(landscapeId: string, actorId: string, updates: ActorUpdate): Promise<ActorLandscape> {
    const res = await api.put<ActorLandscape>(`/actor-landscapes/${landscapeId}/actors/${actorId}`, updates);
    return res.data;
  },

  async extract(landscapeId: string): Promise<ActorLandscape> {
    const res = await api.post<ActorLandscape>(`/actor-landscapes/${landscapeId}/extract`);
    return res.data;
  },

  async normalize(landscapeId: string): Promise<ActorLandscape> {
    const res = await api.post<ActorLandscape>(`/actor-landscapes/${landscapeId}/normalize`);
    return res.data;
  },

  async classifyCompanies(landscapeId: string): Promise<ActorLandscape> {
    const res = await api.post<ActorLandscape>(`/actor-landscapes/${landscapeId}/classify-companies`);
    return res.data;
  },

  async mapTechnology(landscapeId: string): Promise<ActorLandscape> {
    const res = await api.post<ActorLandscape>(`/actor-landscapes/${landscapeId}/map-technology`);
    return res.data;
  },

  async generateReport(landscapeId: string, body?: ReportRequest): Promise<ActorLandscape> {
    const res = await api.post<ActorLandscape>(`/actor-landscapes/${landscapeId}/generate-report`, body ?? {});
    return res.data;
  },

  async runPipeline(landscapeId: string): Promise<ActorLandscape> {
    const res = await api.post<ActorLandscape>(`/actor-landscapes/${landscapeId}/run-pipeline`);
    return res.data;
  },

  exportActorsUrl(landscapeId: string): string {
    return `/api/actor-landscapes/${landscapeId}/export`;
  },
};
